import { isEngineeringRecommendation } from "@productlane/lib/src/agent-recommendations"

import type { AgentItem } from "./agent-items"

export interface BulkPlan {
	// Doc changes and guidance: accepted or declined together.
	suggestions: AgentItem[]
	// A pull request needs its own diff review, an engineering fix its own
	// Linear issue, and a setup step its settings page, so none goes in bulk.
	skipped: AgentItem[]
}

export function bulkPlan(items: readonly AgentItem[]): BulkPlan {
	const plan: BulkPlan = { suggestions: [], skipped: [] }
	for (const item of items) {
		if (item.kind === "code" || item.kind === "setup") plan.skipped.push(item)
		else if (
			item.kind === "recommendation" &&
			isEngineeringRecommendation(item.type)
		)
			plan.skipped.push(item)
		else plan.suggestions.push(item)
	}
	return plan
}

/** The ids a bulk action acts on: the checked rows in list order, or the open one when nothing is checked. */
export function bulkTargets(
	items: readonly AgentItem[],
	checked: ReadonlySet<string>,
	cursorId: string | null,
): AgentItem[] {
	if (checked.size > 0) return items.filter((item) => checked.has(item.id))
	const open = cursorId ? items.find((item) => item.id === cursorId) : null
	return open ? [open] : []
}

/** The proposal group a docs item's accept or decline goes through. */
export function proposalGroupIdOf(item: AgentItem): string | null {
	if (item.kind === "docs") return item.group.id
	if (item.kind === "recommendation" && item.recommendation.type === "CONTENT")
		return item.recommendation.proposalGroupId ?? null
	return null
}

/** Keyboard step: the next or previous item, or the first or last when none is open. */
export function stepTarget(
	keys: readonly string[],
	cursorId: string | null,
	direction: 1 | -1,
): string | null {
	const index = cursorId ? keys.indexOf(cursorId) : -1
	if (index === -1) {
		return direction === 1 ? (keys[0] ?? null) : (keys[keys.length - 1] ?? null)
	}
	return keys[index + direction] ?? null
}

/** A link to a card that was decided or deleted, once both lookups came back empty. */
export function isMissingItem({
	itemId,
	inOpenList,
	listLoaded,
	closedLookup,
}: {
	itemId: string | null
	inOpenList: boolean
	listLoaded: boolean
	// The closed-card lookup: still syncing, or done with or without a row.
	closedLookup: "loading" | "found" | "none"
}): boolean {
	return !!itemId && listLoaded && !inOpenList && closedLookup === "none"
}
